import { z } from "zod";
import type { SectionDef } from "./_types";

const TRUSTED_STORAGE_HOST = "rfmguqpptaynizrrvcxr.supabase.co";
const isTrustedStorage = (u: string) =>
  u.startsWith(`https://${TRUSTED_STORAGE_HOST}/storage/`);

const HEX6 = /^#[0-9A-F]{6}$/i;

const BrandingShape = {
  primary_color:         z.string().regex(HEX6),
  primary_soft_color:    z.string().regex(HEX6).nullable(),
  accent_color:          z.string().regex(HEX6),
  surface_color:         z.string().regex(HEX6),
  surface_alt_color:     z.string().regex(HEX6),
  page_bg_color:         z.string().regex(HEX6),
  text_color:            z.string().regex(HEX6),
  text_muted_color:      z.string().regex(HEX6),
  border_color:          z.string().regex(HEX6),
  border_hover_color:    z.string().regex(HEX6),
  text_on_primary_color: z.string().regex(HEX6),
  logo_light_url:        z.string().url().refine(isTrustedStorage, "must be Supabase Storage").nullable(),
  logo_dark_url:         z.string().url().refine(isTrustedStorage, "must be Supabase Storage").nullable(),
  favicon_url:           z.string().url().refine(isTrustedStorage, "must be Supabase Storage").nullable(),
};

// Klubora platform paper-theme defaults (per brand-manual #FAFAF7 paper +
// #0E1116 ink). Tenants override per their own identity via welcome page.
export const branding = {
  schema: z.object(BrandingShape),
  defaults: {
    primary_color:         "#3D4EE8",
    primary_soft_color:    null,
    accent_color:          "#3D4EE8",
    surface_color:         "#FFFFFF",
    surface_alt_color:     "#F0F0EB",
    page_bg_color:         "#FAFAF7",
    text_color:            "#0E1116",
    text_muted_color:      "#6B6B66",
    border_color:          "#E8E8E3",
    border_hover_color:    "#D0D0CB",
    text_on_primary_color: "#FAFAF7",
    logo_light_url:        null,
    logo_dark_url:         null,
    favicon_url:           null,
  },
  meta: {
    primary_color:         { label: "Primary",          group: "colors", widget: "color" },
    primary_soft_color:    { label: "Primary soft",     group: "colors", widget: "color" },
    accent_color:          { label: "Accent",           group: "colors", widget: "color" },
    surface_color:         { label: "Surface",          group: "colors", widget: "color" },
    surface_alt_color:     { label: "Surface alt",      group: "colors", widget: "color" },
    page_bg_color:         { label: "Page background",  group: "colors", widget: "color" },
    text_color:            { label: "Text",             group: "colors", widget: "color" },
    text_muted_color:      { label: "Text muted",       group: "colors", widget: "color" },
    border_color:          { label: "Border",           group: "colors", widget: "color" },
    border_hover_color:    { label: "Border hover",     group: "colors", widget: "color" },
    text_on_primary_color: { label: "Text on primary",  group: "colors", widget: "color" },
    logo_light_url:        { label: "Logo (light bg)",  group: "logos",  widget: "asset" },
    logo_dark_url:         { label: "Logo (dark bg)",   group: "logos",  widget: "asset" },
    favicon_url:           { label: "Favicon",          group: "logos",  widget: "asset" },
  },
  forbiddenLiterals: [
    "#1A2A52", "#2C3F70", "#C9A96E", "#0E1116",
    "#c9a96e", "#e2c78a",
  ],
} as const satisfies SectionDef<typeof BrandingShape>;
